import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, Clock3, Compass, History, MoonStar, PenLine, Wind } from "lucide-react";
import TopbarInner from "../components/topbar/TopbarInner";
import { useActivity } from "../state/ActivityProvider";
import { getAIDashboardData } from "../services/ai/memory/aiMemory.js";
import { buildConfidenceLine, buildInsights } from "../services/ai/insights/buildInsights.js";
import "./ProgressPage.css";

const DAILY_GOAL_BY_TIME = Object.freeze({
  "30sec": 0.5,
  "2min": 2,
  "5min": 5,
  "10min": 10,
});

const PRIMARY_NEED_DISPLAY = Object.freeze({
  relax: { label: "Détente", Icon: Wind },
  rest: { label: "Repos", Icon: MoonStar },
  recenter: { label: "Recentrage", Icon: Compass },
});

const FR_MONTHS = Object.freeze([
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
]);

const FR_WEEK = Object.freeze(["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]);

function toISOParis(date = new Date()) {
  const formatter = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const [day, month, year] = formatter.format(date).split("/");
  return `${year}-${month}-${day}`;
}

function parseISODate(iso) {
  const [year, month, day] = String(iso).split("-").map(Number);
  return new Date(year, month - 1, day, 12);
}

function toCalendarISO(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function addMonths(date, amount) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1, 12);
}

function startOfWeek(date) {
  const next = new Date(date);
  const mondayOffset = (next.getDay() + 6) % 7;
  next.setDate(next.getDate() - mondayOffset);
  return next;
}

function buildMonthGrid(monthRef) {
  const first = new Date(monthRef.getFullYear(), monthRef.getMonth(), 1, 12);
  const last = new Date(monthRef.getFullYear(), monthRef.getMonth() + 1, 0, 12);
  const prefix = (first.getDay() + 6) % 7;
  const cells = [];

  for (let index = prefix - 1; index >= 0; index -= 1) {
    cells.push({
      date: addDays(first, -(index + 1)),
      inMonth: false,
    });
  }

  for (let day = 1; day <= last.getDate(); day += 1) {
    cells.push({
      date: new Date(monthRef.getFullYear(), monthRef.getMonth(), day, 12),
      inMonth: true,
    });
  }

  while (cells.length < 42) {
    cells.push({
      date: addDays(cells[cells.length - 1].date, 1),
      inMonth: false,
    });
  }

  return cells;
}

function getDailyGoalMinutes(dailyTime) {
  return DAILY_GOAL_BY_TIME[dailyTime] ?? DAILY_GOAL_BY_TIME["2min"];
}

function formatDuration(minutes) {
  const totalSeconds = Math.max(0, Math.round((Number(minutes) || 0) * 60));

  if (totalSeconds < 60) {
    return `${totalSeconds} s`;
  }

  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (totalMinutes < 60) {
    return seconds > 0
      ? `${totalMinutes} min ${seconds} s`
      : `${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutesRemainder = totalMinutes % 60;

  return minutesRemainder > 0
    ? `${hours} h ${minutesRemainder} min`
    : `${hours} h`;
}

function formatProgressLabel(currentMinutes, goalMinutes) {
  const safeCurrent = Math.max(0, Number(currentMinutes) || 0);
  return `${formatDuration(safeCurrent)} / ${formatDuration(goalMinutes)}`;
}

function formatLongDate(iso) {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(parseISODate(iso));
  } catch {
    return iso;
  }
}

function sumMinutes(entries) {
  return entries.reduce(
    (total, entry) => total + Math.max(0, Number(entry?.minutes) || 0),
    0,
  );
}

function isPracticeEntry(entry) {
  return Math.max(0, Number(entry?.minutes) || 0) > 0;
}

function isAuriaEntry(entry) {
  return entry?.meta?.sessionType === "ai-composed" || String(entry?.meta?.trackId || "").startsWith("ai:");
}

function getAuriaIntent(entry) {
  if (!isAuriaEntry(entry)) return null;
  const intent = String(entry?.meta?.trackId || "").split(":")[1];
  return PRIMARY_NEED_DISPLAY[intent] ? intent : null;
}

function getActivityTitle(entry, isPremium = false) {
  if (entry?.type === "ressenti") return "Ressenti";

  if (isAuriaEntry(entry)) {
    if (!isPremium) return getActivityTypeLabel(entry?.type);
    const auriaIntent = getAuriaIntent(entry);
    return auriaIntent ? `${PRIMARY_NEED_DISPLAY[auriaIntent].label} avec Auria` : "Un moment avec Auria";
  }

  return entry?.content || getActivityTypeLabel(entry?.type);
}

function getActivityTypeLabel(type) {
  switch (type) {
    case "listen":
      return "Écoute";
    case "ressenti":
      return "Ressenti";
    case "practice":
      return "Pratique";
    case "breath":
      return "Respiration";
    default:
      return "Activité";
  }
}

function getActivityIcon(type) {
  switch (type) {
    case "ressenti":
      return "✍️";
    case "breath":
      return "🌬️";
    case "practice":
      return "🧘";
    default:
      return "♪";
  }
}

function getCalendarLevel(minutes, dailyGoalMinutes) {
  if (minutes <= 0) return 0;

  const ratio = minutes / Math.max(dailyGoalMinutes, 0.5);

  if (ratio >= 1) return 4;
  if (ratio >= 0.66) return 3;
  if (ratio >= 0.33) return 2;
  return 1;
}

export default function ProgressPage({
  uid = null,
  dailyTime = "2min",
  primaryNeed = "relax",
  dailyProgressMin = 0,
  weeklyProgressMin = 0,
  currentStreak = 0,
  longestStreak = 0,
  isPremium = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { activity, isHydrating, isCloudBacked } = useActivity();

  const todayISO = toISOParis();
  const todayDate = parseISODate(todayISO);

  const [monthRef, setMonthRef] = useState(
    () => new Date(todayDate.getFullYear(), todayDate.getMonth(), 1, 12),
  );
  const [selectedISO, setSelectedISO] = useState(todayISO);
    const [auriaInsights, setAuriaInsights] = useState(null);

  useEffect(() => {
    if (!uid || !isPremium) {
      setAuriaInsights(null);
      return undefined;
    }

    let cancelled = false;

    getAIDashboardData(uid)
      .then((data) => {
        if (cancelled || !data) return;
        const insights = buildInsights(data);
        const confidence = buildConfidenceLine(data, insights);
        setAuriaInsights(insights.length || confidence ? { insights, confidence } : null);
      })
      .catch((error) => {
        console.warn("Statistiques Auria indisponibles.", error);
      });

    return () => {
      cancelled = true;
    };
  }, [uid, isPremium]);

  const safeActivity = useMemo(
    () => (Array.isArray(activity) ? activity : []),
    [activity],
  );

  const byDay = useMemo(() => {
    const map = new Map();

    safeActivity.forEach((entry) => {
      if (!entry?.dateISO) return;
      if (!map.has(entry.dateISO)) map.set(entry.dateISO, []);
      map.get(entry.dateISO).push(entry);
    });

    map.forEach((entries) => {
      entries.sort(
        (left, right) =>
          (Number(right?.createdAt) || 0) - (Number(left?.createdAt) || 0),
      );
    });

    return map;
  }, [safeActivity]);

  const dailyGoalMinutes = getDailyGoalMinutes(dailyTime);
  const weeklyGoalMinutes = dailyGoalMinutes * 7;
  const weekStartDate = startOfWeek(todayDate);
  const weekEndDate = addDays(weekStartDate, 6);
  const weekStartISO = toCalendarISO(weekStartDate);
  const weekEndISO = toCalendarISO(weekEndDate);

  const todayEntries = byDay.get(todayISO) || [];
  const todayActivityMinutes = sumMinutes(todayEntries);

  const currentWeekEntries = useMemo(
    () =>
      safeActivity.filter(
        (entry) =>
          entry?.dateISO &&
          entry.dateISO >= weekStartISO &&
          entry.dateISO <= weekEndISO,
      ),
    [safeActivity, weekEndISO, weekStartISO],
  );

  const currentWeekActivityMinutes = sumMinutes(currentWeekEntries);

  const dayMinutes = Math.max(
    Math.max(0, Number(dailyProgressMin) || 0),
    todayActivityMinutes,
  );
  const weekMinutes = Math.max(
    Math.max(0, Number(weeklyProgressMin) || 0),
    currentWeekActivityMinutes,
  );

  const dailyPercent = Math.min(
    100,
    (dayMinutes / Math.max(dailyGoalMinutes, 0.5)) * 100,
  );
  const weeklyPercent = Math.min(
    100,
    (weekMinutes / Math.max(weeklyGoalMinutes, 0.5)) * 100,
  );
  const dailyGoalReached = dayMinutes >= dailyGoalMinutes;
  const dailyRemaining = Math.max(0, dailyGoalMinutes - dayMinutes);

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) => {
        const date = addDays(weekStartDate, index);
        const iso = toCalendarISO(date);
        const entries = byDay.get(iso) || [];
        const activityMinutes = sumMinutes(entries);
        const minutes =
          iso === todayISO
            ? Math.max(activityMinutes, dayMinutes)
            : activityMinutes;

        return {
          iso,
          shortLabel: FR_WEEK[index],
          dayNumber: date.getDate(),
          minutes,
          isToday: iso === todayISO,
          reachedGoal: minutes >= dailyGoalMinutes,
        };
      }),
    [byDay, dailyGoalMinutes, dayMinutes, todayISO, weekStartDate],
  );

  const activeWeekDays = weekDays.filter((day) => day.minutes > 0).length;

  const practiceEntries = useMemo(
    () => safeActivity.filter(isPracticeEntry),
    [safeActivity],
  );

const auriaEntries = useMemo(
  () => (isPremium ? practiceEntries.filter(isAuriaEntry) : []),
  [isPremium, practiceEntries],
);

  const auriaMinutes = sumMinutes(auriaEntries);
  const auriaDays = useMemo(
    () => new Set(auriaEntries.map((entry) => entry.dateISO).filter(Boolean)),
    [auriaEntries],
  );

  const dominantAuriaIntent = useMemo(() => {
    const totals = new Map();

    auriaEntries.forEach((entry) => {
      const intent = getAuriaIntent(entry);
      if (!intent) return;
      totals.set(intent, (totals.get(intent) || 0) + (Number(entry.minutes) || 0));
    });

    return [...totals.entries()].sort((left, right) => right[1] - left[1])[0]?.[0] || null;
  }, [auriaEntries]);

  const DominantAuriaIcon = dominantAuriaIntent ? PRIMARY_NEED_DISPLAY[dominantAuriaIntent].Icon : null;

  const calendarCells = useMemo(() => buildMonthGrid(monthRef), [monthRef]);
  const selectedEntries = byDay.get(selectedISO) || [];
  const selectedActivityMinutes = sumMinutes(selectedEntries);
  const selectedPracticeMinutes =
    selectedISO === todayISO
      ? Math.max(selectedActivityMinutes, dayMinutes)
      : selectedActivityMinutes;
  const selectedFeelings = selectedEntries.filter(
    (entry) => entry?.type === "ressenti",
  ).length;
  const needMeta =
    PRIMARY_NEED_DISPLAY[primaryNeed] || PRIMARY_NEED_DISPLAY.relax;

  const handleBack = () => {
    if (location.state?.from === "/") {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  };

  const handleMonthChange = (amount) => {
    const nextMonth = addMonths(monthRef, amount);
    setMonthRef(nextMonth);
    setSelectedISO(toCalendarISO(nextMonth));
  };

  const handleWriteFeeling = () => {
    navigate("/ressenti", {
      state: {
        from: "/progression",
      },
    });
  };

  return (
    <>
<TopbarInner
  title="Progression"
  titleColor="#36495B"
  onBack={handleBack}
  onLogoClick={handleBack}
  isPremium={isPremium}
  longestStreak={longestStreak}
/>

      <main className="progress-page">
        <div className="progress-page__inner">
          <header className="page-hero progress-page__intro">
            <p className="page-hero__eyebrow">Votre rythme</p>
            <h2 className="page-hero__title">Progression</h2>
            <p className="page-hero__lead">Un regard simple sur votre pratique : vos objectifs, votre semaine et le chemin déjà parcouru.</p>
          </header>

          <section
            className="progress-overview progress-panel"
            aria-labelledby="progress-today-title"
          >
            <div className="progress-overview__ring">
              <ProgressRing
                percent={dailyPercent}
                label={`${Math.round(dailyPercent)} %`}
                ariaLabel={`Progression quotidienne : ${Math.round(
                  dailyPercent,
                )} pour cent`}
              />
            </div>

            <div className="progress-overview__content">
              <div className="progress-section-heading">
                <p>Aujourd&apos;hui</p>
              </div>

              <h2 id="progress-today-title">
                {formatProgressLabel(dayMinutes, dailyGoalMinutes)}
              </h2>

              <p className="progress-overview__status">
                {dailyGoalReached ? (
                  <>
                    <CheckCircle2 size={17} aria-hidden="true" />
                    Objectif atteint pour aujourd&apos;hui.
                  </>
                ) : (
                  <>
                    <Clock3 size={17} aria-hidden="true" />
                    Encore {formatDuration(dailyRemaining)} pour atteindre votre
                    objectif.
                  </>
                )}
              </p>

              <div className="progress-overview__chips">
                <span className="progress-chip">
                  Objectif quotidien&nbsp;: {formatDuration(dailyGoalMinutes)}
                </span>
                <span className="progress-chip">
                  <needMeta.Icon size={15} strokeWidth={2} aria-hidden="true" />
                  {needMeta.label}
                </span>
              </div>
            </div>
          </section>

          {isPremium && (
            <article className="progress-panel progress-auria" aria-labelledby="progress-auria-title">
              <div className="progress-card-heading">
                <span className="progress-card-heading__icon progress-card-heading__icon--refuge" aria-hidden="true">
                  <img src="/refuge-symbol.svg" alt="" />
                </span>

                <div>
                  <p>Avec Auria</p>
                  <h2 id="progress-auria-title">{formatDuration(auriaMinutes)}</h2>
                </div>
              </div>

              {auriaEntries.length > 0 ? (
                <>
                  <div className="progress-auria__facts">
                    <span>
                      <strong>{auriaDays.size}</strong>
                      jour{auriaDays.size > 1 ? "s" : ""}
                    </span>

                    {dominantAuriaIntent && (
                      <span>
                        <strong><DominantAuriaIcon size={14} strokeWidth={2} aria-hidden="true" /></strong>
                        Surtout {PRIMARY_NEED_DISPLAY[dominantAuriaIntent].label.toLowerCase()}
                      </span>
                    )}
                  </div>

                  {auriaInsights ? (
                    <div className="progress-auria__insights">
                      {auriaInsights.insights.map((insight) => (
                        <p key={insight.text}><span aria-hidden="true">{insight.icon}</span>{insight.text}</p>
                      ))}
                      {auriaInsights.confidence && <p className="progress-auria__confidence">{auriaInsights.confidence}</p>}
                    </div>
                  ) : (
                    <p className="progress-card-note">Les statistiques reprennent uniquement les séances Auria déjà enregistrées.</p>
                  )}
                </>
              ) : (
                <p className="progress-card-note">Les prochaines séances créées avec Auria apparaîtront ici.</p>
              )}
            </article>
          )}

          <section className="progress-stats-grid" aria-label="Résumé de progression">
            <article className="progress-panel progress-week">
              <div className="progress-card-heading">
                <span className="progress-card-heading__icon" aria-hidden="true">
                  <CalendarDays size={19} />
                </span>
                <div>
                  <p>Cette semaine</p>
                  <h2>{formatProgressLabel(weekMinutes, weeklyGoalMinutes)}</h2>
                </div>
              </div>

              <div className="progress-week__bar" aria-hidden="true">
                <span style={{ width: `${weeklyPercent}%` }} />
              </div>

              <div className="progress-week__meta">
                <span>{activeWeekDays} jour{activeWeekDays > 1 ? "s" : ""} actif{activeWeekDays > 1 ? "s" : ""}</span>
                <span>{Math.round(weeklyPercent)} %</span>
              </div>

              <div className="progress-week__days" aria-label="Détail de la semaine">
                {weekDays.map((day) => (
                  <div
                    key={day.iso}
                    className={`progress-week-day ${
                      day.isToday ? "is-today" : ""
                    } ${day.reachedGoal ? "is-complete" : ""}`}
                    title={`${day.shortLabel} ${day.dayNumber} : ${formatDuration(
                      day.minutes,
                    )}`}
                  >
                    <span>{day.shortLabel}</span>
                    <strong>{day.dayNumber}</strong>
                    <i aria-hidden="true" />
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="progress-activity-grid">
            <article
              className="progress-panel progress-calendar"
              aria-labelledby="progress-calendar-title"
            >
              <div className="progress-calendar__header">
                <div className="progress-card-heading">
                  <span className="progress-card-heading__icon" aria-hidden="true">
                    <CalendarDays size={19} />
                  </span>
                  <div>
                    <p>Calendrier</p>
                    <h2 id="progress-calendar-title">
                      {FR_MONTHS[monthRef.getMonth()]} {monthRef.getFullYear()}
                    </h2>
                  </div>
                </div>

                <div className="progress-calendar__navigation">
                  <button
                    type="button"
                    onClick={() => handleMonthChange(-1)}
                    aria-label="Mois précédent"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMonthRef(
                        new Date(
                          todayDate.getFullYear(),
                          todayDate.getMonth(),
                          1,
                          12,
                        ),
                      );
                      setSelectedISO(todayISO);
                    }}
                    className="progress-calendar__today"
                  >
                    Aujourd&apos;hui
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMonthChange(1)}
                    aria-label="Mois suivant"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>

<div className="progress-calendar__legend" aria-label="Légende">
  <span>
    <i className="progress-calendar__dot progress-calendar__dot--practice" />
    Pratique
  </span>

  <span>
    <i className="progress-calendar__dot progress-calendar__dot--feeling" />
    Ressenti
  </span>

  {isPremium && (
    <span>
      <i className="progress-calendar__dot progress-calendar__dot--auria" />
      Auria
    </span>
  )}
</div>

              <div className="progress-calendar__grid">
                {FR_WEEK.map((weekday) => (
                  <span
                    key={weekday}
                    className="progress-calendar__weekday"
                    aria-hidden="true"
                  >
                    {weekday}
                  </span>
                ))}

                {calendarCells.map(({ date, inMonth }) => {
                  const iso = toCalendarISO(date);
                  const entries = byDay.get(iso) || [];
                  const activityMinutes = sumMinutes(entries);
                  const minutes =
                    iso === todayISO
                      ? Math.max(activityMinutes, dayMinutes)
                      : activityMinutes;
                  const hasFeeling = entries.some(
                    (entry) => entry?.type === "ressenti",
                  );
                  const hasAuria = isPremium && entries.some(isAuriaEntry);
                  const level = getCalendarLevel(minutes, dailyGoalMinutes);
                  const isToday = iso === todayISO;
                  const isSelected = iso === selectedISO;

                  return (
                    <button
                      type="button"
                      key={iso}
                      className={`progress-calendar__day ${
                        inMonth ? "" : "is-outside"
                      } ${isToday ? "is-today" : ""} ${
                        isSelected ? "is-selected" : ""
                      }`}
                      data-level={level}
                      onClick={() => setSelectedISO(iso)}
                      aria-pressed={isSelected}
                      aria-label={`${formatLongDate(iso)}. ${
                        minutes > 0
                          ? `${formatDuration(minutes)} de pratique.`
                          : "Aucune pratique."
                      } ${hasFeeling ? "Ressenti enregistré." : ""} ${
                        hasAuria ? "Séance Auria enregistrée." : ""
                      }`}
                    >
                      <span>{date.getDate()}</span>
                      <span className="progress-calendar__day-dots" aria-hidden="true">
                        {minutes > 0 && (
                          <i className="progress-calendar__dot progress-calendar__dot--practice" />
                        )}
                        {hasFeeling && (
                          <i className="progress-calendar__dot progress-calendar__dot--feeling" />
                        )}
                        {hasAuria && (
                          <i className="progress-calendar__dot progress-calendar__dot--auria" />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </article>

            <article
              className="progress-panel progress-journal"
              aria-labelledby="progress-journal-title"
            >
              <div className="progress-journal__header">
                <div className="progress-card-heading">
                  <span className="progress-card-heading__icon" aria-hidden="true">
                    <History size={19} />
                  </span>
                  <div>
                    <p>Journal</p>
                    <h2 id="progress-journal-title">
                      {formatLongDate(selectedISO)}
                    </h2>
                  </div>
                </div>

                <button
                  type="button"
                  className="progress-journal__write"
                  onClick={handleWriteFeeling}
                >
                  <PenLine size={16} />
                  <span>Écrire</span>
                </button>
              </div>

              <div className="progress-journal__summary">
                <span>{formatDuration(selectedPracticeMinutes)} de pratique</span>
                <span>
                  {selectedFeelings} ressenti{selectedFeelings > 1 ? "s" : ""}
                </span>
              </div>

              {isHydrating ? (
                <div className="progress-journal__empty" aria-live="polite">
                  Synchronisation de votre activité…
                </div>
              ) : selectedEntries.length > 0 ? (
                <div className="progress-journal__list">
                  {selectedEntries.map((entry, index) => {
                    const isFeeling = entry?.type === "ressenti";

                    return (
                      <article
                        className="progress-journal-entry"
                        key={
                          entry?.id ||
                          `${entry?.dateISO || selectedISO}-${entry?.type || "entry"}-${index}`
                        }
                      >
                        <span
                          className="progress-journal-entry__icon"
                          data-type={entry?.type || "activity"}
                          aria-hidden="true"
                        >
                          {getActivityIcon(entry?.type)}
                        </span>

                        <div className="progress-journal-entry__body">
                          <div className="progress-journal-entry__top">
                            <strong>{getActivityTitle(entry, isPremium)}</strong>
                          </div>

                          {isFeeling ? (
                            <p>{entry?.content || "Ressenti enregistré."}</p>
                          ) : (
                            <p>
                              {getActivityTypeLabel(entry?.type)}
                              {Number(entry?.minutes) > 0
                                ? ` · ${formatDuration(entry.minutes)}`
                                : ""}
                            </p>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="progress-journal__empty">
                  <span aria-hidden="true">○</span>
                  <strong>Aucune activité ce jour-là</strong>
                  <p>
                    Vos prochaines écoutes, respirations et ressentis apparaîtront
                    ici.
                  </p>
                </div>
              )}

              <p className="progress-journal__sync">
                {isCloudBacked
                  ? "Activité synchronisée avec votre compte."
                  : "Activité conservée sur cet appareil."}
              </p>
            </article>
          </section>
        </div>
      </main>
    </>
  );
}

function ProgressRing({
  percent = 0,
  label,
  ariaLabel,
  size = 170,
  stroke = 11,
}) {
  const safePercent = Math.max(0, Math.min(100, Number(percent) || 0));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (safePercent / 100) * circumference;

  return (
    <div
      className="progress-ring"
      role="img"
      aria-label={ariaLabel}
      style={{
        "--progress-ring-size": `${size}px`,
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <defs>
          <linearGradient
            id="progressPageRingGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#76A5A7" />
            <stop offset="100%" stopColor="#4B7CF6" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="progress-ring__track"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="progress-ring__value"
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>

      <span className="progress-ring__label">{label}</span>
    </div>
  );
}
